import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Chip, Typography, IconButton,
  FormControlLabel, Switch, Autocomplete, Alert, Snackbar, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import useIsMobile from '../hooks/useIsMobile';
import ReglePieceEditor from './ReglePieceEditor';
import { VisaCatalogue, listerVisasCatalogue } from '../services/alertesReglesServices';
import {
  NormePj, NormePjInput, ReglePiece, CleFixe, ChampObserve, CLES_FIXES, LIMITES, REGEX_CLE_CHAMP,
  estCleFixe, regleParDefaut, nettoyerRegle, validerRegle, creerNormePj, majNormePj, champsObserves, messageErreur
} from '../services/normesPjServices';

// Création / modification d'une norme de pièces jointes (menu « Pièces jointes »).
// Un titre de visa n'appartient qu'à une seule norme : les titres déjà pris par une autre norme sont grisés
// (le backend refuse aussi, 409). Les trois règles fixes (photo, passeport, autres pièces) s'activent par un
// interrupteur ; leurs valeurs restent en mémoire si on les désactive puis réactive.

interface Props {
  open: boolean;
  norme: NormePj | null; // null = création
  normes: NormePj[];     // toutes les normes, pour griser les visas déjà rattachés
  onClose: () => void;
  onSaved: () => void;
}

const TITRES_FIXES: Record<CleFixe, { titre: string; aide: string }> = {
  photo: { titre: 'Photo du voyageur', aide: 'Fichier photo.* du profil voyageur (quel que soit son vrai format).' },
  passeport: { titre: 'Passeport du voyageur', aide: 'Fichier passeport.* du profil voyageur.' },
  autres: {
    titre: 'Autres pièces du formulaire',
    aide: 'Toute pièce jointe du dossier sans règle propre (billet, réservation, lettre…). ecard.pdf et visa.pdf sont ignorés.',
  },
};

// Comparaison des titres comme le backend : casse et accents ignorés
const normaliser = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

type ParCle<T> = Record<CleFixe, T>;
const reglesFixesParDefaut = (): ParCle<ReglePiece> => ({
  photo: regleParDefaut('photo'),
  passeport: regleParDefaut('passeport'),
  autres: regleParDefaut('autres'),
});

const NormePjDialog: React.FC<Props> = ({ open, norme, normes, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [libelle, setLibelle] = useState('');
  const [titres, setTitres] = useState<string[]>([]);
  const [motifTous, setMotifTous] = useState('');
  const [motifUnParmi, setMotifUnParmi] = useState('');
  const [note, setNote] = useState('');
  const [actif, setActif] = useState(true);
  const [fixes, setFixes] = useState<ParCle<ReglePiece>>(reglesFixesParDefaut());
  const [fixesActives, setFixesActives] = useState<ParCle<boolean>>({ photo: true, passeport: true, autres: true });
  const [specifiques, setSpecifiques] = useState<ReglePiece[]>([]);
  const [visas, setVisas] = useState<VisaCatalogue[]>([]);
  const [champs, setChamps] = useState<ChampObserve[]>([]);
  const [champsLoading, setChampsLoading] = useState(false);
  const [nouvelleCle, setNouvelleCle] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const catalogueCharge = useRef(false);

  // Initialisation à chaque ouverture (création : les trois règles fixes actives, sans limites)
  useEffect(() => {
    if (!open) return;
    const f = reglesFixesParDefaut();
    const actives: ParCle<boolean> = { photo: !norme, passeport: !norme, autres: !norme };
    const s: ReglePiece[] = [];
    if (norme) {
      setLibelle(norme.libelle || '');
      setTitres(norme.visas_titres || []);
      setMotifTous(norme.motif_tous || '');
      setMotifUnParmi(norme.motif_un_parmi || '');
      setNote(norme.note || '');
      setActif(Number(norme.actif) === 1);
      (norme.pieces || []).forEach((r) => {
        const complete = { ...regleParDefaut(r.cle), ...r };
        if (estCleFixe(r.cle)) {
          f[r.cle] = complete;
          actives[r.cle] = true;
        } else {
          s.push(complete);
        }
      });
    } else {
      setLibelle('');
      setTitres([]);
      setMotifTous('');
      setMotifUnParmi('');
      setNote('');
      setActif(true);
    }
    setFixes(f);
    setFixesActives(actives);
    setSpecifiques(s);
    setChamps([]);
    setNouvelleCle('');
  }, [norme, open]);

  // Catalogue des visas chargé une seule fois, à la première ouverture
  useEffect(() => {
    if (!open || catalogueCharge.current) return;
    catalogueCharge.current = true;
    listerVisasCatalogue()
      .then(setVisas)
      .catch((e) => {
        console.error('Erreur chargement catalogue visas', e);
        catalogueCharge.current = false;
        setToast({ msg: 'Chargement du catalogue des visas impossible', sev: 'error' });
      });
  }, [open]);

  // Options : titres non vides, dédoublonnés, triés par pays puis titre (MUI exige un tri par groupe pour groupBy)
  const options = useMemo(() => {
    const vus = new Set<string>();
    const uniques: VisaCatalogue[] = [];
    visas.forEach((v) => {
      if (!v.titre || vus.has(v.titre)) return;
      vus.add(v.titre);
      uniques.push(v);
    });
    return uniques.sort(
      (a, b) => a.libelle_pays.localeCompare(b.libelle_pays) || (a.titre ?? '').localeCompare(b.titre ?? '')
    );
  }, [visas]);

  const valeur = useMemo(() => options.filter((v) => titres.includes(v.titre ?? '')), [options, titres]);

  // Titres stockés mais absents du catalogue (visa renommé) : conservés, retirables volontairement
  const orphelins = useMemo(
    () => (visas.length ? titres.filter((t) => !options.some((v) => v.titre === t)) : []),
    [visas, options, titres]
  );

  // Titre normalisé -> libellé de l'AUTRE norme qui le détient
  const prisPar = useMemo(() => {
    const m = new Map<string, string>();
    normes
      .filter((n) => !norme || n.id !== norme.id)
      .forEach((n) => (n.visas_titres || []).forEach((t) => m.set(normaliser(t), n.libelle)));
    return m;
  }, [normes, norme]);

  const pieces = useMemo(
    () => [...CLES_FIXES.filter((c) => fixesActives[c]).map((c) => fixes[c]), ...specifiques],
    [fixes, fixesActives, specifiques]
  );
  const reglesInvalides = pieces.some((r) => validerRegle(r).length > 0);
  const chevrons = /[<>]/.test(libelle + note + motifTous + motifUnParmi);
  const libelleOk = !!libelle.trim() && libelle.trim().length <= LIMITES.libelleNorme;
  const valide = libelleOk && !chevrons && pieces.length > 0 && pieces.length <= LIMITES.regles && !reglesInvalides;
  const sansRattachement = !titres.length && !motifTous.trim() && !motifUnParmi.trim();

  // Clés déjà utilisées (casse ignorée) : les trois clés fixes et les règles par type de pièce
  const clesPrises = useMemo(
    () => new Set<string>([...CLES_FIXES, ...specifiques.map((r) => r.cle.toLowerCase())]),
    [specifiques]
  );
  const cleSaisie = nouvelleCle.trim();
  const cleValide = REGEX_CLE_CHAMP.test(cleSaisie) && !clesPrises.has(cleSaisie.toLowerCase());
  const aideCle = !cleSaisie
    ? 'Nom du champ du formulaire client, visible au début du nom des PJ (billetAvion-…)'
    : !REGEX_CLE_CHAMP.test(cleSaisie)
      ? 'Lettres, chiffres et _, commence par une lettre'
      : clesPrises.has(cleSaisie.toLowerCase()) ? 'Cette pièce a déjà sa règle' : ' ';

  // Types de pièces vus dans les dossiers correspondant au ciblage actuel (chargés à l'ouverture de la liste)
  const chargerChamps = async () => {
    if (champsLoading) return;
    setChampsLoading(true);
    try {
      const r = await champsObserves({ visas_titres: titres, motif_tous: motifTous.trim(), motif_un_parmi: motifUnParmi.trim() });
      setChamps(r.champs);
    } catch (e) {
      console.error('Erreur chargement des types de pièces', e);
      setToast({ msg: messageErreur(e, 'Types de pièces indisponibles'), sev: 'error' });
    } finally {
      setChampsLoading(false);
    }
  };

  const ajouterSpecifique = (cle: string) => {
    const c = cle.trim();
    if (!REGEX_CLE_CHAMP.test(c) || clesPrises.has(c.toLowerCase())) return;
    setSpecifiques((prev) => [...prev, regleParDefaut(c)]);
    setNouvelleCle('');
  };

  const handleSave = async () => {
    if (!valide) return;
    setSaving(true);
    const input: NormePjInput = {
      libelle: libelle.trim(),
      visas_titres: titres,
      motif_tous: motifTous.trim(),
      motif_un_parmi: motifUnParmi.trim(),
      pieces: pieces.map(nettoyerRegle),
      note: note.trim(),
      actif,
    };
    try {
      if (norme) await majNormePj(norme.id, input);
      else await creerNormePj(input);
      setToast({ msg: 'Norme enregistrée', sev: 'success' });
      onSaved();
      onClose();
    } catch (e) {
      console.error('Erreur enregistrement norme', e);
      setToast({ msg: messageErreur(e, "Erreur lors de l'enregistrement"), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const optionsCles = champs.filter((c) => !clesPrises.has(c.cle.toLowerCase())).map((c) => c.cle);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Box sx={{ flexGrow: 1 }}>{norme ? 'Modifier la norme' : 'Nouvelle norme'}</Box>
          <FormControlLabel
            control={<Switch checked={actif} onChange={(e) => setActif(e.target.checked)} />}
            label={actif ? 'Active' : 'Désactivée'}
          />
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 1. Nom */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Nom</Typography>
            <TextField
              label="Nom de la norme"
              placeholder="Normes eVisa Inde"
              required
              fullWidth
              size="small"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              error={/[<>]/.test(libelle) || libelle.trim().length > LIMITES.libelleNorme}
              helperText={/[<>]/.test(libelle) ? 'Les caractères < et > sont interdits' : `${libelle.trim().length}/${LIMITES.libelleNorme}`}
            />
          </Box>

          {/* 2. Quels dossiers ? */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Quels dossiers ?</Typography>
            <Autocomplete
              multiple
              size="small"
              options={options}
              value={valeur}
              groupBy={(v) => v.libelle_pays}
              getOptionLabel={(v) => v.titre ?? ''}
              isOptionEqualToValue={(a, b) => a.titre === b.titre}
              getOptionDisabled={(v) => prisPar.has(normaliser(v.titre ?? ''))}
              renderOption={(props, v) => {
                const autre = prisPar.get(normaliser(v.titre ?? ''));
                return (
                  <li {...props} key={v.titre ?? ''}>
                    {v.titre}
                    {autre && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (déjà dans « {autre} »)
                      </Typography>
                    )}
                  </li>
                );
              }}
              onChange={(_, vals) => {
                const conserves = titres.filter((t) => !options.some((v) => v.titre === t));
                setTitres([...conserves, ...vals.map((v) => v.titre ?? '')]);
              }}
              ChipProps={{ size: 'small' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visas du catalogue"
                  helperText="Le dossier est rattaché quand le libellé de son visa est exactement l'un de ces titres. Un visa n'appartient qu'à une seule norme."
                />
              )}
            />
            {orphelins.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {orphelins.map((t) => (
                  <Chip
                    key={t}
                    size="small"
                    color="warning"
                    label={`“${t}” introuvable dans le catalogue`}
                    onDelete={() => setTitres((prev) => prev.filter((x) => x !== t))}
                  />
                ))}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 1 }}>
              …et/ou par mots-clés dans le libellé du visa, <strong>seulement pour les dossiers hors catalogue</strong> :
              formulaires externes (« eVisa Inde 30 jours »), dossiers créés à la main (libellé = nom du pays).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Mots-clés tous obligatoires"
                placeholder="inde"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                value={motifTous}
                onChange={(e) => setMotifTous(e.target.value)}
                error={/[<>]/.test(motifTous)}
                helperText={/[<>]/.test(motifTous) ? 'Les caractères < et > sont interdits' : 'Séparés par des virgules'}
              />
              <TextField
                label="Au moins un de ces mots-clés"
                placeholder="evisa"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                value={motifUnParmi}
                onChange={(e) => setMotifUnParmi(e.target.value)}
                error={/[<>]/.test(motifUnParmi)}
                helperText={/[<>]/.test(motifUnParmi) ? 'Les caractères < et > sont interdits' : 'Séparés par des virgules'}
              />
            </Box>
            {sansRattachement && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Sans visa ni mot-clé, la norme ne s'applique qu'en la choisissant à la main dans la fenêtre « Normes PJ »
                d'un dossier.
              </Alert>
            )}
          </Box>

          {/* 3. Pièces */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2">Pièces</Typography>
            {CLES_FIXES.map((c) => (
              <Box key={c}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={fixesActives[c]}
                      onChange={(e) => setFixesActives((prev) => ({ ...prev, [c]: e.target.checked }))}
                    />
                  }
                  label={fixesActives[c] ? `${TITRES_FIXES[c].titre} : contrôlée` : `${TITRES_FIXES[c].titre} : non contrôlée`}
                />
                {fixesActives[c] && (
                  <ReglePieceEditor
                    titre={TITRES_FIXES[c].titre}
                    aide={TITRES_FIXES[c].aide}
                    regle={fixes[c]}
                    onChange={(r) => setFixes((prev) => ({ ...prev, [c]: r }))}
                  />
                )}
              </Box>
            ))}

            {specifiques.map((r, i) => (
              <ReglePieceEditor
                key={r.cle}
                titre={`Pièce « ${r.cle} »`}
                aide={`PJ du dossier dont le nom commence par ${r.cle} (prioritaire sur « Autres pièces »).`}
                regle={r}
                onChange={(nr) => setSpecifiques((prev) => prev.map((x, j) => (j === i ? nr : x)))}
                onSupprimer={() => setSpecifiques((prev) => prev.filter((_, j) => j !== i))}
              />
            ))}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Autocomplete
                freeSolo
                size="small"
                sx={{ flex: 1, minWidth: 260 }}
                options={optionsCles}
                loading={champsLoading}
                loadingText="Recherche des types de pièces dans les dossiers…"
                noOptionsText="Aucun autre type de pièce vu dans les dossiers de ces visas"
                onOpen={() => { if (!champs.length) chargerChamps(); }}
                inputValue={nouvelleCle}
                onInputChange={(_, v) => setNouvelleCle(v)}
                onChange={(_, v) => { if (typeof v === 'string') ajouterSpecifique(v); }}
                renderOption={(props, cle) => {
                  const vu = champs.find((c) => c.cle === cle);
                  return (
                    <li {...props} key={cle}>
                      {cle}
                      {vu && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({vu.nb} dossier{vu.nb > 1 ? 's' : ''})
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ajouter une règle pour un type de pièce"
                    placeholder="lettreInvitation"
                    error={!!cleSaisie && !cleValide}
                    helperText={aideCle}
                  />
                )}
              />
              <Button startIcon={<AddIcon />} disabled={!cleValide} onClick={() => ajouterSpecifique(nouvelleCle)} sx={{ mt: 0.5 }}>
                Ajouter
              </Button>
            </Box>
            {pieces.length === 0 && (
              <Typography variant="caption" color="error">Activez au moins une règle de pièce.</Typography>
            )}
          </Box>

          {/* 4. Note */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Note</Typography>
            <TextField
              label="Note interne (optionnel)"
              placeholder="Source des normes, date de vérification…"
              multiline
              minRows={2}
              fullWidth
              size="small"
              inputProps={{ maxLength: LIMITES.note }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              error={/[<>]/.test(note)}
              helperText={/[<>]/.test(note) ? 'Les caractères < et > sont interdits' : `${note.length}/${LIMITES.note}`}
            />
          </Box>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Dans Demandes, le bouton « Normes PJ » d'un dossier contrôle ses pièces avec cette norme et produit des
            versions conformes téléchargeables ; les originaux ne sont jamais modifiés.
          </Typography>
        </DialogContent>

        <DialogActions>
          {!valide && pieces.length > 0 && reglesInvalides && (
            <Typography variant="caption" color="error" sx={{ mr: 'auto', ml: 1 }}>Corriger les règles signalées en rouge.</Typography>
          )}
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!valide || saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default NormePjDialog;
